import { useId, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { Customer } from '@tailoring-crm/shared';
import { CustomerForm } from './customer-form.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@tailoring-crm/ui';

/**
 * Adding a customer without leaving what you were doing.
 *
 * The counter is halfway through a booking, the person in front of it is not in the book yet,
 * and making the owner navigate away to a page and find their way back would lose the order
 * they were taking. So this exists alongside `CustomerFormPage` rather than instead of it, and
 * it is a shell around the very same `CustomerForm` — the fields, the validation, the shop's
 * field order and the duplicate answer are one implementation, so a customer added here and a
 * customer added on the page are the same record made the same way.
 *
 * What the dialog does NOT show is the part of the record that has nowhere to go in a modal:
 * the photograph, the measurements and the order history, all of which belong to a customer who
 * already exists and are on their page.
 */

export interface CustomerFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present for an edit; absent for a create. */
  customer?: Customer | null;
  /**
   * What the counter had already typed when it decided to create. A digits-only search becomes
   * the mobile number, anything else becomes the name — so the form opens half-filled and the
   * owner types the other half.
   */
  prefillQuery?: string;
  onSaved?: (customer: Customer) => void;
  /** The counter chose to open the customer the duplicate check found instead. */
  onOpenExisting?: (customerId: string) => void;
}

export function CustomerFormDialog({
  open,
  onOpenChange,
  customer = null,
  prefillQuery = '',
  onSaved,
  onOpenExisting,
}: CustomerFormDialogProps): ReactElement {
  const { t } = useTranslation();
  const fieldId = useId();
  const isEdit = customer !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={`${fieldId}-description`}>
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t('customers.editTitle', { name: customer.name })
              : t('customers.createTitle')}
          </DialogTitle>
          <DialogDescription id={`${fieldId}-description`}>
            {isEdit ? t('customers.editDescription') : t('customers.createDescription')}
          </DialogDescription>
        </DialogHeader>

        {/* Keyed on the customer so reopening for somebody else remounts the form rather than
            showing the previous person's details for a frame. */}
        <CustomerForm
          key={customer?.id ?? 'new'}
          variant="dialog"
          customer={customer}
          prefillQuery={prefillQuery}
          onSaved={(saved) => {
            onSaved?.(saved);
            onOpenChange(false);
          }}
          onOpenExisting={(customerId) => {
            onOpenExisting?.(customerId);
            onOpenChange(false);
          }}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
